import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { updatePaymentLinks } from '../../actions/payment-links';
import {
  Modal,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  CustomInput,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import DatePicker from '../date-time-picker';
import {
  GQL_PAYMENTLINK_CREATE_BY_SLUG,
  GQL_PAYMENTLINK_UPDATE_BY_SLUG
} from '../../gqls';
import {
  validator,
  paymentLinkUrl,
  getUserTimezone,
  sanitizeDOM,
  REGEX_SLUG,
  slug,
  isJSON
} from '../../utils';

import Icon from '../icon';
import moment from 'moment-timezone';
import SubmitButton from '../submit-button';
import RichEditor, { getPlainText, getContentString, getEditorContent } from '../rich-text-editor';
import { EditorState } from 'draft-js';
import StreetMapsInput from '../street-maps-input';
import MultipleImageVideoInput from '../multiple-image-video-input';
import MayarAlert from '../alert/alert';

const ModalBodyEvent = ({ isOpen = false, onClose, onSuccess, data }) => {
  if (!isOpen) return null;

  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only
  const [insertPaymentLink, { data: newpl, loading: submitCreateLoading }] = useMutation(
    GQL_PAYMENTLINK_CREATE_BY_SLUG
  );
  const [editPaymentLink, { loading: submitUpdateLoading }] = useMutation(GQL_PAYMENTLINK_UPDATE_BY_SLUG);

  const today = new Date();
  const tomorrow = new Date(today);
  const { register, handleSubmit, errors, control, setValue } = useForm();
  const [multipleImageId, setMultipleImageId] = useState('');

  const [submitError, setSubmitError] = useState(null);

  const [date, setDate] = useState(data ? data.expiredAt : null);
  const [eventStartAt, setEventStartAt] = useState(data ? data.eventStartAt : null);
  const [eventEndAt, setEventEndAt] = useState(data ? data.eventEndAt : null);
  const [link, setLink] = useState(data ? data.link : '');
  const [onlineEvent, setOnlineEvent] = useState(data?.event?.isOnlineEvent || false);
  const [selectPosition, setSelectPosition] = useState(() => {
    if (data?.event?.mapLocation) {
      try {
        return JSON.parse(data.event.mapLocation);
      } catch (e) {
        return data.event.mapLocation;
      }
    }
    return null;
  });

  const stateEdit = EditorState.createEmpty();
  const [editorState, setEditorState] = useState(stateEdit);
  const [description, setDescription] = useState('');
  const [textDescription, setTextDescription] = useState('');
  const [check, setCheck] = useState(true);
  const [invalidDescription, setInvalidDescription] = useState(false);
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);


  const dispatch = useDispatch();
  const isUpdate = !data ? false : true;

  const useCharCounter = (initial = 0) => {
    const [count, setCount] = useState(initial);
    const onChange = e => setCount(e.target.value.length);
    return [count, onChange];
  };

  const [nameLength, onNameChange] = useCharCounter(data?.name?.length || 0);
  const [eventAddressLength, setEventAddressLength] = useState(0);
  const [notesLength, onNotesChange] = useCharCounter(data?.notes?.length || 0);
  const [termsLength, onTermsChange] = useCharCounter(data?.terms?.length || 0);

  if (!data) data = {};

  useEffect(() => {
    if (!onlineEvent && data?.event?.mapLocation) {
      try {
        const parsed = JSON.parse(data.event.mapLocation);
        setSelectPosition(parsed);
        setValue('mapLocation', {
          value: data.event.mapLocation,
          label: parsed?.display_name || ''
        });
      } catch (e) {
        setSelectPosition(data.event.mapLocation);
        setValue('mapLocation', {
          value: typeof data.event.mapLocation === 'string' ? data.event.mapLocation : JSON.stringify(data.event.mapLocation),
          label: data.event.mapLocation?.display_name || ''
        });
      }
    }
  }, [data, onlineEvent, setValue]);

  useEffect(() => {
    if (!onlineEvent && data?.event?.eventAddress) {
      let address = data.event.eventAddress;

      if (isJSON(address)) {
        try {
          address = JSON.parse(address)?.display_name || '';
        } catch (e) {
          address = '';
        }
      }

      setEventAddressLength(address.length);
    }
  }, [data, onlineEvent]);

  const setEditor = content => {
    setEditorState(content);
    setDescription(getContentString(content));
    setTextDescription(getPlainText(content));
  };

  useEffect(() => {
    if (textDescription.length <= 0 && !check) {
      setInvalidDescription(true);
    } else {
      setInvalidDescription(false);
    }
  }, [textDescription]);

  useEffect(() => {
    if (isUpdate) {
      const content = getEditorContent(data.description);
      setEditor(content);
    }
  }, [data]);

  const mapState = {
    onlineEvent,
    errors,
    control,
    register,
    selectPosition,
    setSelectPosition,
    data,
    fieldName: 'mapLocation'
  };

  const exceed = (value, max) => value && value.length > max;

  const onSubmit = async values => {
    if (textDescription.length <= 0) {
      setCheck(false);
      setInvalidDescription(true);
      return;
    }

    if (exceed(values.name, 100)) {
      setSubmitError(true);
      return;
    }

    if (!values.isOnlineEvent && exceed(values.eventAddress, 300)) {
      setSubmitError(true);
      return;
    }

    if (values.isOnlineEvent && exceed(values.eventAddress, 255)) {
      setSubmitError(true);
      return;
    }

    if (exceed(values.notes, 500)) {
      setSubmitError(true);
      return;
    }

    if (exceed(values.terms, 1000)) {
      setSubmitError(true);
      return;
    }

    if (exceed(values.redirectUrl, 255)) {
      setSubmitError(true);
      return;
    }

    if (date !== null) setDate(date);
    if (eventStartAt !== null) setEventStartAt(eventStartAt);
    if (eventEndAt !== null) setEventEndAt(eventEndAt);
    if (values.affiliateCommission) values.affiliateCommission = parseInt(values.affiliateCommission);
    else values.affiliateCommission = null;

    if (!values.isOnlineEvent && selectPosition) {
      values.mapLocation = JSON.stringify(selectPosition);
    } else {
      values.mapLocation = null;
    }

    const formValues = {
      ...values,
      description: sanitizeDOM(description),
      event: {
        eventAddress: values.eventAddress,
        isOnlineEvent: values.isOnlineEvent,
        maxTicket: Number(values.maxTicket),
        mapLocation: values.mapLocation
      }
    };

    delete formValues.isOnlineEvent;
    delete formValues.eventAddress;
    delete formValues.mapLocation;
    delete formValues.maxTicket;

    try {
      if (isUpdate) {
        let statusUpdate = 'closed';
        if (!date || new Date(date) > Date.now()) {
          statusUpdate = 'active';
        }
        const objeditPaymentLinkBySlug = {
          ...formValues,
          id: data.id,
          expiredAt: date ? new Date(date).setHours(0, 0, 0, 0) : eventStartAt,
          eventStartAt: eventStartAt,
          eventEndAt: eventEndAt,
          status: statusUpdate,
          eventUrl: values.eventAddress,
          event: {
            ...formValues.event
          },
          ...(deletedCoverContents.length && {
            deletedCoverContents: deletedCoverContents.map(item => {
              if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                return {
                  videoMultipleFileId: item?.id
                };
              }
              return {
                imageId: item.editImage?.id || item.id
              };
            })
          }),
          ...(multipleCoverContent.length && {
            orderedMultipleCoverContent: multipleCoverContent.map(item => {
              if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                return {
                  position: Number(item.position),
                  videoId: item.videoId || item.file?.id || item.value,
                  editId: item?.id
                };
              }
              return {
                position: Number(item.position),
                imageId: item.editImage?.id || item.id
              };
            })
          })
        };

        if (multipleImageId) {
          objeditPaymentLinkBySlug.multipleImageId = multipleImageId;
        }
        await editPaymentLink({
          variables: {
            input: objeditPaymentLinkBySlug
          }
        });
        dispatch(updatePaymentLinks(Math.random()));
      } else {
        const objPaymentLink = {
          ...formValues,
          expiredAt: date ? new Date(date).setHours(0, 0, 0, 0) : eventStartAt,
          link: slug(values.name),
          type: 'event',
          status: 'active',
          eventStartAt: eventStartAt,
          eventEndAt: eventEndAt,
          eventUrl: values.eventAddress,
          event: {
            ...formValues.event
          },
          ...(deletedCoverContents.length && {
            deletedCoverContents: deletedCoverContents.map(item => {
              if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
                return {
                  videoMultipleFileId: item?.id
                };
              }
              return {
                imageId: item.editImage?.id || item.id
              };
            })
          }),
          ...(isolatedMultipleCoverContent.length && {
            orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
              if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
                return {
                  position: Number(item.position),
                  videoId: item.videoId || item.file?.id || item.value,
                  editId: item?.id
                };
              }
              return {
                position: Number(item.position),
                imageId: item.editImage?.id || item.id
              };
            })
          })
        };

        if (multipleImageId) {
          objPaymentLink.multipleImageId = multipleImageId;
        }
        await insertPaymentLink({
          variables: {
            input: objPaymentLink
          }
        });
        //segment tracking
        window.analytics.track('Create Event', values);
      }
    } catch (error) {
      setSubmitError(error.message || "Error! Silahkan coba lagi atau hubungi CS");
      window.analytics.track('Create Event - ERROR', values);
    }
  };

  if (newpl && newpl.insertPaymentLinkBySlug) {
    onSuccess(newpl.insertPaymentLinkBySlug.id);
  }


  return (
    <Modal isOpen={isOpen} fade size="lg">
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Event / Acara</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>
      <ModalBody>
        <p>
          <small>Kelola event, pendaftaran peserta dan penjualan tiket secara otomatis dengan Mayar</small>
        </p>
        <hr />
        {submitError && <MayarAlert isError message={submitError} />}
        <Form onSubmit={handleSubmit(onSubmit)} id="create-payment-link">
          <FormGroup>
            <Label for="name">Nama Event / Acara*</Label>
            <Input
              type="text"
              name="name"
              id="name"
              maxLength={100}
              invalid={errors.name}
              defaultValue={data.name}
              innerRef={register({
                required: validator.required,
                minLength: validator.minLength(5),
                maxLength: {
                  value: 100,
                  message: 'Maksimal 100 karakter'
                }
              })}
              onChange={onNameChange}
            />
            <div className="d-flex justify-content-end mt-1">
              <small className={nameLength >= 100 ? 'text-danger' : 'text-muted'}>{nameLength}/100</small>
            </div>
            <FormFeedback>{errors.name?.message}</FormFeedback>
          </FormGroup>
          {isUpdate && (
            <FormGroup>
              <Label for="name">Slug*</Label>
              <Input
                type="text"
                name="link"
                id="link"
                invalid={errors.link}
                defaultValue={data.link}
                onChange={e => setLink(e.target.value)}
                innerRef={register({
                  required: validator.required,
                  pattern: validator.pattern(REGEX_SLUG)
                })}
              />
              <FormFeedback>{errors.link?.message}</FormFeedback>
              <small>{'contoh : ' + paymentLinkUrl(link)}</small>
            </FormGroup>
          )}
          <FormGroup>
            <Label for="description">Deskripsi*</Label>
            <RichEditor onEditorStateChange={setEditor} editorState={editorState} invalidState={invalidDescription} />
            <FormFeedback className={`${invalidDescription ? 'd-block' : ''}`}>Wajib diisi</FormFeedback>
          </FormGroup>

          <FormGroup>
            <CustomInput
              id="formSwitch1"
              label="EVENT ONLINE"
              name="isOnlineEvent"
              type="switch"
              color="primary"
              defaultChecked={data?.event?.isOnlineEvent}
              innerRef={register()}
              onChange={e => setOnlineEvent(e.target.checked)}
            />
          </FormGroup>

          {onlineEvent ? (
            <FormGroup>
              <Label for="eventAddress">Event URL/Link (Zoom/Google Meet/Youtube Live, dll)</Label>
              <Input
                type="text"
                name="eventAddress"
                id="eventAddress"
                maxLength={255}
                defaultValue={data.eventUrl}
                innerRef={register({
                  pattern: validator.url,
                  maxLength: {
                    value: 255,
                    message: 'Maksimal 255 karakter'
                  }
                })}
              />
              <FormFeedback>{errors?.eventAddress?.message}</FormFeedback>
            </FormGroup>
          ) : (
            <>
              <FormGroup>
                <Label for="eventAddress">Lokasi / Alamat Venue</Label>
                <Input
                  type="textarea"
                  name="eventAddress"
                  id="eventAddress"
                  maxLength={300}
                  invalid={errors.eventAddress}
                  defaultValue={
                    isJSON(data?.event?.eventAddress)
                      ? JSON.parse(data?.event?.eventAddress)?.display_name
                      : data?.event?.eventAddress
                  }
                  innerRef={register({
                    maxLength: {
                      value: 300,
                      message: 'Maksimal 300 karakter'
                    }
                  })}
                  onChange={e => setEventAddressLength(e.target.value.length)}
                />
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted">
                    Tulis lokasi acara selengkapnya (nama venue, jalan, kota, provinsi)
                  </small>

                  <small className={eventAddressLength >= 300 ? 'text-danger' : 'text-muted'}>
                    {eventAddressLength}/300
                  </small>
                </div>
                <FormFeedback>{errors.eventAddress?.message}</FormFeedback>
              </FormGroup>

              <StreetMapsInput state={mapState} />
            </>
          )}

          <FormGroup>
            <Label for="eventStartAt">Waktu Mulai*</Label>
            <div className="mb-1 date-block">
              <DatePicker
                showTimeSelect
                placeholderText="Pilih Tanggal dan Jam"
                dateFormat="yyyy/MM/dd HH:mm"
                timeFormat="HH:mm"
                selected={eventStartAt ? new Date(eventStartAt) : null}
                minDate={new Date()}
                required
                className="rui-datetimepicker form-control"
                onChange={d => setEventStartAt(d)}
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Label for="eventEndAt">Waktu Selesai</Label>
            <div className="mb-1 date-block">
              <DatePicker
                showTimeSelect
                placeholderText="Pilih Tanggal dan Jam"
                dateFormat="yyyy/MM/dd HH:mm"
                timeFormat="HH:mm"
                selected={eventEndAt ? new Date(eventEndAt) : null}
                minDate={new Date()}
                className="rui-datetimepicker form-control"
                onChange={d => setEventEndAt(d)}
              />
            </div>
            <small>
              Timezone menggunakan {moment().tz(getUserTimezone()).format('z')} (GMT
              {moment().tz(getUserTimezone()).format('Z')}) - {moment.tz.guess()}
            </small>
          </FormGroup>

          <MultipleImageVideoInput
            productName={data.name}
            setMultipleImageId={setMultipleImageId}
            multipleImage={data.multipleImage}
            onLoading={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isAutoUploadOnSelectFile={false}
            maxVideo={1}
            maxImage={5}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />

          <FormGroup>
            <Label for="notes">Instruksi</Label>
            <Input
              type="textarea"
              name="notes"
              id="notes"
              maxLength={500}
              defaultValue={data.notes}
              innerRef={register({
                maxLength: {
                  value: 500,
                  message: 'Maksimal 500 karakter'
                }
              })}
              onChange={onNotesChange}
            />

            <div className="d-flex justify-content-between mt-1">
              <small className="text-muted">Instruksi setelah pendaftaran / pembayaran</small>
              <small className={notesLength >= 500 ? 'text-danger' : 'text-muted'}>{notesLength}/500</small>
            </div>

            <FormFeedback>{errors.notes?.message}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="terms">Syarat dan Ketentuan</Label>
            <Input
              type="textarea"
              name="terms"
              id="terms"
              maxLength={1000}
              defaultValue={data.terms}
              innerRef={register({
                maxLength: {
                  value: 1000,
                  message: 'Maksimal 1000 karakter'
                }
              })}
              onChange={onTermsChange}
            />
            <div className="d-flex justify-content-end mt-1">
              <small className={termsLength >= 1000 ? 'text-danger' : 'text-muted'}>{termsLength}/1000</small>
            </div>
            <FormFeedback>{errors.terms?.message}</FormFeedback>
          </FormGroup>
          <hr />
          <FormGroup>
            <CustomInput
              id="formSwitch2"
              label="1 akun email - 1 kali transaksi "
              name="oneAccountTransaction"
              type="switch"
              color="primary"
              defaultChecked={data?.oneAccountTransaction}
              innerRef={register()}
            />
          </FormGroup>
          <FormGroup>
            <Label for="startAt"> Waktu Mulai Penjualan</Label>
            <div className="mb-1">
              <Controller
                name="startAt"
                control={control}
                defaultValue={data.startAt || null}
                render={({ onChange, name, value }) => (
                  <DatePicker
                    id={name}
                    placeholderText="Pilih Tanggal Mulai"
                    showTimeSelect
                    dateFormat="yyyy/MM/dd HH:mm"
                    timeFormat="HH:mm"
                    selected={value ? new Date(value) : null}
                    minDate={new Date()}
                    className={`rui-datetimepicker form-control w-auto mb-1 ${!!errors.startAt ? 'is-invalid' : ''}`}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <FormFeedback className="d-block">{errors.startAt?.message}</FormFeedback>
            <small>
              Kami akan membuka link pembayaran pada tanggal dan waktu yang anda pilih. Opsional, kosongkan untuk
              langsung membuka penjualan
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="kadaluarsa4">Tanggal Pendaftaran Ditutup</Label>
            <div className="mb-1">
              <DatePicker
                placeholderText="Pilih tanggal atau kosongkan"
                dateFormat="yyyy/MM/dd"
                selected={date ? new Date(date) : null}
                minDate={tomorrow.setDate(tomorrow.getDate() + 1)}
                className="rui-datetimepicker form-control w-auto"
                onChange={d => setDate(d)}
              />
            </div>
            <small>
              Kami akan menutup pendaftaran pada tanggal ini (opsional), jika tidak diisi kami akan menutup event ini
              setelah acara dimulai.
            </small>
          </FormGroup>
          <FormGroup>
            <Label for="maxTicket">Jumlah maksimal tiket per transaksi</Label>
            <Input
              innerRef={register()}
              defaultValue={data?.event?.maxTicket || 50}
              id="maxTicket"
              name="maxTicket"
              type="select"
            >
              {Array.from(Array(50).keys()).map(item => (
                <option key={item} value={item + 1}>
                  {item + 1} Tiket
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="redirectUrl">Redirect URL</Label>
            <Input
              type="text"
              name="redirectUrl"
              id="redirectUrl"
              maxLength={255}
              defaultValue={data.redirectUrl}
              innerRef={register({
                pattern: validator.url,
                maxLength: {
                  value: 255,
                  message: 'Maksimal 255 karakter'
                }
              })}
            />
            <FormFeedback>{errors?.redirectUrl?.message}</FormFeedback>
            <small>Pelanggan akan dibawa kehalaman ini setelah membayar (opsional /bisa dikosongkan)</small>
          </FormGroup>

          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>

                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}

          <SubmitButton
            size="lg"
            block
            color="brand"
            type="submit"
            className="text-center"
            loading
            isLoading={submitCreateLoading || submitUpdateLoading}
            isDisabled={disableSubmitButton}
            text={`${isUpdate ? 'Update' : 'Buat'} Event`}
          />
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalBodyEvent;
